import React, { useState } from 'react';
import { Upload, File, Download, Trash2 } from 'lucide-react';
import DragDropZone from '../DragDropZone';

export default function SongFilesPanel({
  song,
  isMobile,
  loading,
  onUploadSongFile,
  onDeleteSongFile,
  onPreviewFile,
}) {
  const [pendingFile, setPendingFile] = useState(null);
  const [fileTitle, setFileTitle] = useState('');
  const [uploading, setUploading] = useState(false);

  const filesArray = song.attachments
    ? Object.keys(song.attachments)
        .map(id => ({ id, ...song.attachments[id] }))
        .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate))
    : [];

  const handleSelect = (file) => {
    setPendingFile(file);
    setFileTitle(file.name.replace(/\.[^/.]+$/, ''));
  };

  const handleUpload = async () => {
    if (!pendingFile || !fileTitle.trim()) return;
    setUploading(true);
    const ok = await onUploadSongFile(song.id, pendingFile, fileTitle.trim());
    setUploading(false);
    if (ok) {
      setPendingFile(null);
      setFileTitle('');
    }
  };

  return (
    <div className="space-y-3">
      {!pendingFile ? (
        <DragDropZone
          accept=".docx,.pdf,.txt,.md"
          onFileSelect={handleSelect}
          disabled={loading || uploading}
          icon={Upload}
          message="Drop a file (.docx, .pdf, .txt, .md)"
          isMobile={isMobile}
        />
      ) : (
        <div className="border border-surface-border-light rounded-card-btn p-3">
          <p className="text-sm text-text-muted mb-2">
            Selected: <span className="font-semibold">{pendingFile.name}</span>
          </p>
          <input
            type="text"
            value={fileTitle}
            onChange={(e) => setFileTitle(e.target.value)}
            placeholder="Title for this file…"
            className="w-full bg-transparent border border-surface-border-light px-3 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand text-sm mb-2 rounded-pill"
          />
          <div className="flex gap-2">
            <button
              onClick={handleUpload}
              disabled={uploading || !fileTitle.trim()}
              className="bg-brand hover:bg-brand-hover disabled:opacity-disabled transition px-3 py-1.5 text-xs font-semibold rounded-pill flex items-center gap-1"
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            <button
              onClick={() => { setPendingFile(null); setFileTitle(''); }}
              disabled={uploading}
              className="bg-white/10 hover:bg-white/20 transition px-3 py-1.5 text-xs font-semibold rounded-pill"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {filesArray.length === 0 ? (
        <p className="text-text-subtle text-xs italic">No files attached yet.</p>
      ) : (
        <div className="space-y-2">
          {filesArray.map(f => (
            <div key={f.id} className="border border-surface-border-light rounded-card-btn p-2.5 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate">{f.title}</p>
                <p className="text-xs text-text-subtle truncate">{f.fileName} • {f.fileSize} KB</p>
              </div>
              <div className="flex gap-1 ml-2 flex-shrink-0">
                <button
                  onClick={() => onPreviewFile(f)}
                  className="bg-transparent hover:bg-brand transition p-1.5 rounded-full"
                  title="Preview"
                >
                  <File className="w-3.5 h-3.5 text-white" />
                </button>
                <a
                  href={f.fileURL}
                  download={f.fileName}
                  className="bg-transparent hover:bg-brand transition p-1.5 rounded-full"
                  title="Download"
                >
                  <Download className="w-3.5 h-3.5 text-white" />
                </a>
                <button
                  onClick={async () => {
                    if (window.confirm(`Delete "${f.title}"?`)) {
                      await onDeleteSongFile(song.id, f.id, f.storagePath);
                    }
                  }}
                  className="bg-transparent hover:bg-red-600 transition p-1.5 rounded-full"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
